import React, {
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
  useRef,
} from "react";
import { useParams, useHistory } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";

// Services & Types
import { MemberService } from "services/member.service";
import { DepartmentService } from "services/department.service";
import { EcodimeClassService } from "services/ecodime-class.service";
import {
  IDepartmentResponse,
  ISaveMemberDTO,
  IUpdateMemberDTO,
  IMemberResponseDTO,
  Gender,
  MaritalStatus,
  IDepartmentFlatDTO,
} from "models/church.types";

export default function MemberRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<
    { value: number; label: string }[]
  >([]);
  const [parentOptions, setParentOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [ecodimeClasses, setEcodimeClasses] = useState<
    { value: number; label: string }[]
  >([]);

  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const maritalStatusOptions = [
    { value: MaritalStatus.SINGLE, label: "Célibataire" },
    { value: MaritalStatus.MARRIED, label: "Marié(e)" },
    { value: MaritalStatus.DIVORCED, label: "Divorcé(e)" },
    { value: MaritalStatus.WIDOWED, label: "Veuf/Veuve" },
  ];

  const [member, setMember] = useState<any>({
    firstName: "",
    lastName: "",
    gender: Gender.MALE,
    maritalStatus: MaritalStatus.SINGLE,
    marriageDate: "",
    email: "",
    phoneNumber: "",
    departmentIds: [],
    parentId: undefined,
    isChild: false,
    ecodimeClassId: undefined,
    birthDate: "",
    joinDate: new Date().toISOString().split("T")[0],
    photoLink: "",
  });

  const customSelectStyles = (hasError: boolean) => ({
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: state.isFocused
        ? "#D4AF37"
        : hasError
          ? "#ef4444"
          : "#334155",
      borderRadius: "0.5rem",
      padding: "3px",
      minHeight: "45px",
      color: "white",
      boxShadow: "none",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#D4AF37" },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 9999,
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#D4AF37" : "transparent",
      color: state.isFocused ? "#0F172A" : "white",
      padding: "10px",
      cursor: "pointer",
      fontSize: "0.875rem",
      "&:active": { backgroundColor: "#D4AF37" },
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      borderRadius: "0.375rem",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: "#D4AF37",
      fontSize: "0.75rem",
      fontWeight: "600",
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: "#94A3B8",
      "&:hover": {
        backgroundColor: "#ef4444",
        color: "white",
      },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
      fontSize: "0.875rem",
    }),
    input: (base: any) => ({ ...base, color: "white" }),
    placeholder: (base: any) => ({
      ...base,
      color: "#64748b",
      fontSize: "0.875rem",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#475569",
      "&:hover": { color: "#D4AF37" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
  });

  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      try {
        const [deptData, membersRes, ecodimeData] = await Promise.all([
          DepartmentService.getAllSimple(),
          MemberService.getAll(0, 1000),
          EcodimeClassService.getAllSimple(),
        ]);

        setDepartments(
          deptData.map((d: IDepartmentResponse) => ({
            value: d.id,
            label: d.name,
          })),
        );

        setParentOptions(
          membersRes.data
            .filter(
              (m: IMemberResponseDTO) =>
                !m.isChild && (isEditMode ? m.id !== parseInt(id!) : true),
            )
            .map((m: IMemberResponseDTO) => ({
              value: m.id,
              label: `${m.lastName || ""} ${m.firstName || ""}`.trim(),
            })),
        );

        setEcodimeClasses(
          ecodimeData.map((c: any) => ({
            value: c.id,
            label: c.name,
          })),
        );

        if (isEditMode && id) {
          const data: IMemberResponseDTO & {
            ecodimeClassId?: number;
            ecodimeClass?: { id: number };
            marriageDate?: string;
          } = await MemberService.getById(parseInt(id));

          const classId = data.ecodimeClassId || data.ecodimeClass?.id;
          
          const deptIds = data.departments
            ? data.departments.map((d: IDepartmentFlatDTO) => d.id)
            : [];

          setMember({
            id: data.id,
            firstName: data.firstName,
            lastName: data.lastName,
            gender: data.gender || Gender.MALE,
            maritalStatus: data.maritalStatus || MaritalStatus.SINGLE,
            marriageDate: data.marriageDate ? data.marriageDate.split("T")[0] : "",
            email: data.email || "",
            phoneNumber: data.phoneNumber || "",
            departmentIds: deptIds,
            parentId: data.parentId,
            isChild: data.isChild || false,
            ecodimeClassId: classId || undefined,
            birthDate: data.birthDate ? data.birthDate.split("T")[0] : "",
            joinDate: data.joinDate ? data.joinDate.split("T")[0] : "",
            photoLink: data.photoLink || "",
          });

          if (data.photoLink) {
            const baseUrl =
              process.env.REACT_APP_API_URL || window.location.origin;
            const cleanedBaseUrl = baseUrl.replace(/\/api$/, "");

            setPhotoPreview(
              data.photoLink.startsWith("http")
                ? data.photoLink
                : `${cleanedBaseUrl}${data.photoLink}`,
            );
          }
        }
      } catch (error) {
        toast.error("Erreur lors du chargement des dépendances");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, [id, isEditMode]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!member.firstName) newErrors.firstName = "Le prénom est requis";
    if (!member.lastName) newErrors.lastName = "Le nom est requis";

    if (!member.isChild && !member.phoneNumber) {
      newErrors.phoneNumber = "Le téléphone est requis pour un adulte";
    }

    if (member.isChild && !member.parentId) {
      newErrors.parentId =
        "Un parent ou tuteur responsable doit être sélectionné";
    }

    if (!member.birthDate) {
      newErrors.birthDate = "La date de naissance est requise";
    } else {
      const selectedDate = new Date(member.birthDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        newErrors.birthDate =
          "La date de naissance ne peut pas être dans le futur";
      }
    }

    if (
      !member.isChild &&
      member.maritalStatus === MaritalStatus.MARRIED &&
      member.marriageDate
    ) {
      const marriage = new Date(member.marriageDate);
      const today = new Date();
      if (marriage > today) {
        newErrors.marriageDate = "La date de mariage ne peut pas être dans le futur";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setMember((prev: any) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePhotoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
        setMember((prev: any) => ({
          ...prev,
          photoLink: reader.result as string,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Fonction pour supprimer la photo de profil
  const handleRemovePhoto = (e: React.MouseEvent) => {
    e.stopPropagation(); // Évite de déclencher l'ouverture de l'explorateur de fichiers du parent
    setPhotoPreview(null);
    setMember((prev: any) => ({
      ...prev,
      photoLink: "",
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Réinitialise l'input file
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire");
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        ...member,
        birthDate: new Date(member.birthDate).toISOString().split("T")[0],
        joinDate: member.joinDate
          ? new Date(member.joinDate).toISOString().split("T")[0]
          : null,
        maritalStatus: member.isChild ? null : member.maritalStatus,
        marriageDate:
          !member.isChild &&
          member.maritalStatus === MaritalStatus.MARRIED &&
          member.marriageDate
            ? new Date(member.marriageDate).toISOString().split("T")[0]
            : null,
        parentId: member.isChild ? member.parentId : null,
        departments:
          !member.isChild && member.departmentIds?.length > 0
            ? member.departmentIds.map((deptId: number) => ({ id: deptId }))
            : [],
        ecodimeClass:
          member.isChild && member.ecodimeClassId
            ? { id: member.ecodimeClassId }
            : null,
      };

      if (isEditMode) {
        await MemberService.update({
          ...payload,
          id: member.id,
        } as IUpdateMemberDTO);
        toast.success("Fiche membre mise à jour avec succès !");
      } else {
        await MemberService.create(payload as ISaveMemberDTO);
        toast.success("Nouveau membre enregistré avec succès !");
      }

      setTimeout(() => {
        history.push("/admin/members");
      }, 1000);
    } catch (error: any) {
      console.error(error);
      const errorMessage =
        error.response?.data?.title ||
        "Une erreur est survenue lors de l'enregistrement";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen mt-3 pt-24 items-center justify-start px-4">
      <div className="w-full max-w-4xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl">
          {/* Header */}
          <div className="rounded-t-xl bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-user-circle mr-2 text-[#D4AF37]"></i>
                {isEditMode ? "Édition du membre" : "Formulaire d'inscription"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit} noValidate>
              {/* SECTION PHOTO */}
              <div className="flex items-center gap-6 mb-10 p-4 bg-[#0F172A]/50 rounded-lg border border-slate-800">
                <div className="relative">
                  <div
                    className="relative w-20 h-20 rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center cursor-pointer hover:border-[#D4AF37] overflow-hidden group"
                    onClick={() => fileInputRef.current?.click()}
                    title="Cliquer pour changer la photo"
                  >
                    {photoPreview ? (
                      <img
                        src={photoPreview}
                        className="w-full h-full object-cover"
                        alt="Avatar"
                      />
                    ) : (
                      <i className="fas fa-camera text-slate-500 text-xl group-hover:text-[#D4AF37] transition-colors"></i>
                    )}
                  </div>

                  {/* Bouton de suppression de la photo */}
                  {photoPreview && (
                    <button
                      type="button"
                      onClick={handleRemovePhoto}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center text-xs shadow-md transition-transform hover:scale-110"
                      title="Supprimer la photo"
                    >
                      <i className="fas fa-trash-alt text-[10px]"></i>
                    </button>
                  )}
                </div>

                <div>
                  <h4 className="text-white text-sm font-bold">
                    Photo de profil
                  </h4>
                  <p className="text-slate-500 text-[10px] uppercase font-black tracking-tighter">
                    JPG, PNG | MAX 2MB
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Catégorie / Profil (Adulte vs Enfant) */}
                <div className="md:col-span-2">
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-cubes mr-2"></i>Catégorie d'âge
                  </label>
                  <div className="flex gap-4">
                    {[
                      { v: false, l: "Adulte (Majeur)", i: "user-tie" },
                      { v: true, l: "Enfant (Écodime)", i: "child" },
                    ].map((cat) => (
                      <label
                        key={cat.l}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border cursor-pointer transition-all ${member.isChild === cat.v ? "bg-[#D4AF37] text-[#0F172A] border-[#D4AF37]" : "bg-[#0F172A] text-slate-400 border-slate-700 hover:border-slate-500"}`}
                      >
                        <input
                          type="radio"
                          name="isChild"
                          checked={member.isChild === cat.v}
                          onChange={() => {
                            setMember({
                              ...member,
                              isChild: cat.v,
                              parentId: undefined,
                              departmentIds: [],
                              ecodimeClassId: undefined,
                              marriageDate: "",
                            });
                            setErrors({});
                          }}
                          className="hidden"
                        />
                        <i className={`fas fa-${cat.i}`}></i>
                        <span className="text-[10px] font-black uppercase">
                          {cat.l}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Prénom */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-user mr-2"></i>Prénom
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    autoComplete="given-name"
                    value={member.firstName}
                    onChange={handleChange}
                    className={`w-full bg-[#0F172A] border ${errors.firstName ? "border-red-500" : "border-slate-700"} rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all shadow-inner`}
                  />
                  {errors.firstName && (
                    <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                      {errors.firstName}
                    </span>
                  )}
                </div>

                {/* Nom */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-signature mr-2"></i>Nom
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    autoComplete="family-name"
                    value={member.lastName}
                    onChange={handleChange}
                    className={`w-full bg-[#0F172A] border ${errors.lastName ? "border-red-500" : "border-slate-700"} rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all shadow-inner`}
                  />
                  {errors.lastName && (
                    <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                      {errors.lastName}
                    </span>
                  )}
                </div>

                {/* Sexe */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-venus-mars mr-2"></i>Sexe
                  </label>
                  <div className="flex gap-4">
                    {[
                      { v: Gender.MALE, l: "Masculin", i: "mars" },
                      { v: Gender.FEMALE, l: "Féminin", i: "venus" },
                    ].map((g) => (
                      <label
                        key={g.v}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border cursor-pointer transition-all ${member.gender === g.v ? "bg-[#D4AF37] text-[#0F172A] border-[#D4AF37]" : "bg-[#0F172A] text-slate-400 border-slate-700 hover:border-slate-500"}`}
                      >
                        <input
                          type="radio"
                          name="gender"
                          value={g.v}
                          checked={member.gender === g.v}
                          onChange={(e) =>
                            setMember({
                              ...member,
                              gender: e.target.value as Gender,
                            })
                          }
                          className="hidden"
                        />
                        <i className={`fas fa-${g.i}`}></i>
                        <span className="text-[10px] font-black uppercase">
                          {g.l}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Téléphone */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-phone mr-2"></i>Téléphone{" "}
                    {!member.isChild && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    autoComplete="tel"
                    value={member.phoneNumber}
                    onChange={handleChange}
                    className={`w-full bg-[#0F172A] border ${errors.phoneNumber ? "border-red-500" : "border-slate-700"} rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all shadow-inner`}
                  />
                  {errors.phoneNumber && (
                    <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                      {errors.phoneNumber}
                    </span>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-envelope mr-2"></i>Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={member.email}
                    onChange={handleChange}
                    className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all"
                  />
                </div>

                {/* Date de Naissance */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-birthday-cake mr-2"></i>Date de
                    Naissance
                  </label>
                  <input
                    type="date"
                    name="birthDate"
                    autoComplete="bday"
                    value={member.birthDate}
                    onChange={handleChange}
                    className={`w-full bg-[#0F172A] border ${errors.birthDate ? "border-red-500" : "border-slate-700"} rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all [color-scheme:dark]`}
                  />
                  {errors.birthDate && (
                    <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                      {errors.birthDate}
                    </span>
                  )}
                </div>

                {/* Date d'Intégration */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                    <i className="fas fa-calendar-check mr-2"></i>Date
                    d'intégration
                  </label>
                  <input
                    type="date"
                    name="joinDate"
                    value={member.joinDate}
                    onChange={handleChange}
                    className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all [color-scheme:dark]"
                  />
                </div>

                {/* BLOC CONDITIONNEL ADULTE */}
                {!member.isChild && (
                  <>
                    {/* Situation Matrimoniale */}
                    <div>
                      <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                        <i className="fas fa-heart mr-2"></i>Situation
                        Matrimoniale
                      </label>
                      <Select
                        options={maritalStatusOptions}
                        styles={customSelectStyles(false)}
                        placeholder="Choisir le statut..."
                        value={maritalStatusOptions.find(
                          (opt) => opt.value === member.maritalStatus,
                        )}
                        onChange={(selected) => {
                          const newStatus = selected
                            ? selected.value
                            : MaritalStatus.SINGLE;
                          setMember({
                            ...member,
                            maritalStatus: newStatus,
                            marriageDate:
                              newStatus === MaritalStatus.MARRIED
                                ? member.marriageDate
                                : "",
                          });
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>

                    {/* Champ dynamique Date de Mariage si Marié(e) */}
                    {member.maritalStatus === MaritalStatus.MARRIED && (
                      <div>
                        <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                          <i className="fas fa-ring mr-2"></i>Date de Mariage
                        </label>
                        <input
                          type="date"
                          name="marriageDate"
                          value={member.marriageDate}
                          onChange={handleChange}
                          className={`w-full bg-[#0F172A] border ${errors.marriageDate ? "border-red-500" : "border-slate-700"} rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all [color-scheme:dark]`}
                        />
                        {errors.marriageDate && (
                          <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                            {errors.marriageDate}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Sélection Multiple des Départements */}
                    <div className={member.maritalStatus === MaritalStatus.MARRIED ? "md:col-span-2" : ""}>
                      <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                        <i className="fas fa-users mr-2"></i>Département(s)
                        d'activité
                      </label>
                      <Select
                        isMulti
                        options={departments}
                        styles={customSelectStyles(false)}
                        placeholder="Sélectionner un ou plusieurs départements..."
                        value={departments.filter((d) =>
                          member.departmentIds?.includes(d.value),
                        )}
                        onChange={(selectedOptions) => {
                          const selectedIds = selectedOptions
                            ? selectedOptions.map((opt) => opt.value)
                            : [];
                          setMember({
                            ...member,
                            departmentIds: selectedIds,
                          });
                        }}
                        noOptionsMessage={() => "Aucun département trouvé"}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </>
                )}

                {/* BLOC CONDITIONNEL ENFANT */}
                {member.isChild && (
                  <>
                    <div className="md:col-span-2">
                      <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                        <i className="fas fa-user-shield mr-2"></i>Parent /
                        Tuteur Légal Responsable{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <Select
                        options={parentOptions}
                        styles={customSelectStyles(Boolean(errors.parentId))}
                        isClearable
                        placeholder="Rechercher le membre parent (Majeur)..."
                        value={
                          parentOptions.find(
                            (p) => p.value === member.parentId,
                          ) || null
                        }
                        onChange={(selected) => {
                          setMember({
                            ...member,
                            parentId: selected ? selected.value : undefined,
                          });
                          if (errors.parentId)
                            setErrors((prev) => ({ ...prev, parentId: "" }));
                        }}
                        noOptionsMessage={() => "Aucun membre majeur trouvé"}
                        menuPortalTarget={document.body}
                      />
                      {errors.parentId && (
                        <span className="text-red-500 text-[9px] font-bold uppercase ml-1 mt-1 block">
                          {errors.parentId}
                        </span>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 block mb-2">
                        <i className="fas fa-graduation-cap mr-2"></i>Classe
                        Écodime
                      </label>
                      <Select
                        options={ecodimeClasses}
                        styles={customSelectStyles(false)}
                        isClearable
                        placeholder="Sélectionner la classe Écodime..."
                        value={
                          ecodimeClasses.find(
                            (c) => c.value === member.ecodimeClassId,
                          ) || null
                        }
                        onChange={(selected) => {
                          setMember({
                            ...member,
                            ecodimeClassId: selected
                              ? selected.value
                              : undefined,
                          });
                        }}
                        noOptionsMessage={() => "Aucune classe trouvée"}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-12 flex justify-end gap-4 border-t border-slate-700/50 pt-6">
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  className="text-slate-400 font-bold text-xs uppercase px-4 py-2 hover:text-white transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Enregistrer la fiche"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}